import React, { useState } from "react";
import { CountryCodeDropdown } from "./CountryCodeDropdown";
import { defaultDialCode } from "../lib/visitor-country";
import { UserInfoCopy } from "../lib/quick-actions";
import { isLightColor } from "../lib/is-light-color";

// Type for the data passed to parent component - includes combined phone field
export type UserInfoFormValues = {
  fullName: string;
  countryCode: string;
  phoneNumber: string;
  phone: string; // Combined countryCode  phoneNumber
  email?: string;
};

interface CustomColors {
  backgroundColor?: string;
  textColor?: string;
  secondaryTextColor?: string;
  borderColor?: string;
  inputBackgroundColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
}

interface UserInfoFormProps {
  onSubmit: (values: UserInfoFormValues) => void;
  onSkip?: () => void;
  /** Email becomes a required, validated field (default false = optional). */
  requireEmail?: boolean;
  /** Localisable labels/placeholders/errors; absent fields use the English
   *  defaults, so widgets without the config render exactly as before. */
  copy?: UserInfoCopy;
  theme?: "light" | "dark";
  customColors?: CustomColors;
  /** ISO2 country from IP geolocation — regional selector default. */
  visitorCountry?: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = {
  fullName?: string;
  countryCode?: string;
  phoneNumber?: string;
  email?: string;
};

const validate = (
  values: {
    fullName: string;
    countryCode: string;
    phoneNumber: string;
    email: string;
  },
  requireEmail: boolean,
  copy?: UserInfoCopy,
): FieldErrors => {
  const errors: FieldErrors = {};
  if (values.fullName.trim().length < 2) {
    errors.fullName = copy?.name_error || "Full name is required";
  }
  if (!values.countryCode) {
    errors.countryCode = copy?.phone_error || "Country code is required";
  }
  if (values.phoneNumber.length < 5) {
    errors.phoneNumber = copy?.phone_error || "Phone number is required";
  } else if (values.phoneNumber.length > 30) {
    errors.phoneNumber = "Phone number must be 30 digits or less";
  } else if (!/^[0-9]+$/.test(values.phoneNumber)) {
    errors.phoneNumber = "Phone number must contain only digits";
  }
  if (requireEmail && !values.email.trim()) {
    errors.email = copy?.email_error || "Email is required";
  } else if (values.email && !EMAIL_REGEX.test(values.email)) {
    errors.email = copy?.email_error || "Invalid email address";
  }
  return errors;
};

/** Label row + control + error. Module-level so React keeps the inputs
 *  mounted between renders (an inline component would remount on every
 *  keystroke and drop focus). */
const Field: React.FC<{
  label: string;
  hint?: string;
  error?: string;
  labelClass: string;
  labelStyle: React.CSSProperties;
  children: React.ReactNode;
}> = ({ label, hint, error, labelClass, labelStyle, children }) => (
  <div>
    <div className="mb-1.5 flex items-baseline justify-between">
      <label className={`text-[13px] font-medium ${labelClass}`} style={labelStyle}>
        {label}
      </label>
      {hint && (
        <span className={`text-[12px] ${labelClass} opacity-80`} style={labelStyle}>
          {hint}
        </span>
      )}
    </div>
    {children}
    {error && <p className="mt-1.5 text-[12px] font-medium text-red-500">{error}</p>}
  </div>
);

/**
 * Pre-chat contact form. One flat surface (no card-in-a-card), 44px
 * rounded fields with a hairline ring that becomes the brand accent on
 * focus, country code + number joined into a single control, a full-width
 * brand button and a quiet text-link Skip.
 */
export function UserInfoForm({
  onSubmit,
  onSkip,
  requireEmail = false,
  copy,
  theme = "dark",
  customColors = {},
  visitorCountry,
}: UserInfoFormProps) {
  const [fullName, setFullName] = useState("");
  // Regional default: IP country → browser locale → "27" (see visitor-country.ts)
  const [countryCode, setCountryCode] = useState(() => defaultDialCode(visitorCountry));
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});

  // A field's error clears the moment the visitor edits it.
  const clearError = (...keys: (keyof FieldErrors)[]) =>
    setErrors((prev) => {
      if (!keys.some((k) => prev[k])) return prev;
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });

  // Function to mask phone input - remove spaces and non-numeric characters
  const maskPhoneInput = (value: string): string => {
    return value.replace(/[^0-9]/g, "");
  };

  // Handle form submission and combine phone fields
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const fieldErrors = validate(
      { fullName, countryCode, phoneNumber, email },
      requireEmail,
      copy,
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    const localNumber = phoneNumber.replace(/^0+/, "");
    const combinedPhone = `${countryCode}${localNumber}`;

    onSubmit({
      fullName,
      countryCode,
      phoneNumber,
      phone: combinedPhone,
      email: email || undefined,
    });
  };

  // The real surface decides light/dark: `theme` defaults to "dark" for
  // every org that never set it, even when its custom colours are white.
  const light = isLightColor(customColors.backgroundColor) ?? theme === "light";
  const accent = customColors.buttonBackgroundColor || "#2563eb";

  const t = light
    ? {
        bg: "bg-white",
        title: "text-gray-900",
        label: "text-gray-600",
        field:
          "bg-white text-gray-900 ring-1 ring-black/[0.09] placeholder:text-gray-400 hover:ring-black/[0.16]",
        fieldError: "ring-red-400",
        divider: "bg-black/[0.09]",
        topBorder: "border-black/[0.06]",
        skip: "text-gray-500 hover:text-gray-800",
      }
    : {
        bg: "bg-black",
        title: "text-white",
        label: "text-zinc-400",
        field:
          "bg-zinc-900 text-white ring-1 ring-white/10 placeholder:text-zinc-500 hover:ring-white/20",
        fieldError: "ring-red-500",
        divider: "bg-white/10",
        topBorder: "border-white/10",
        skip: "text-zinc-400 hover:text-zinc-200",
      };

  // Shell shared by the plain inputs and the joined phone control. The
  // accent ring is a CSS variable so the brand colour reaches Tailwind.
  const shell =
    "w-full rounded-xl transition-[box-shadow] duration-150 ease-out focus-within:ring-2 focus-within:ring-[color:var(--uif-accent)]";
  const textInput =
    "h-11 w-full min-w-0 rounded-xl bg-transparent px-3.5 text-base md:text-[15px] leading-none outline-none focus:outline-none";

  const formStyles = {
    backgroundColor: customColors.backgroundColor,
    color: customColors.textColor,
    borderColor: customColors.borderColor,
    "--uif-accent": accent,
  } as React.CSSProperties;

  const fieldStyles: React.CSSProperties = {
    backgroundColor: customColors.inputBackgroundColor,
    color: customColors.textColor,
  };

  const labelStyle: React.CSSProperties = {
    color: customColors.secondaryTextColor || customColors.textColor,
  };

  const buttonStyles: React.CSSProperties = {
    backgroundColor: accent,
    color: customColors.buttonTextColor || "#ffffff",
  };

  const emailOptional = !requireEmail && !copy?.email_label;
  const phoneError = errors.phoneNumber || errors.countryCode;

  return (
    <div className={`border-t px-4 pt-4 pb-4 ${t.bg} ${t.topBorder}`} style={formStyles}>
      <h3
        className={`mb-3.5 text-[15px] font-semibold tracking-[-0.01em] ${t.title}`}
        style={{ color: customColors.textColor }}
      >
        {copy?.title || "Please provide your contact information"}
      </h3>

      <form onSubmit={handleFormSubmit} className="space-y-3.5" noValidate>
        <Field label={copy?.name_label || "Full name"} error={errors.fullName} labelClass={t.label} labelStyle={labelStyle}>
          <div className={`${shell} ${t.field} ${errors.fullName ? t.fieldError : ""}`} style={fieldStyles}>
            <input
              className={textInput}
              placeholder={copy?.name_placeholder || "Your name"}
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                clearError("fullName");
              }}
              autoComplete="name"
              style={{ color: customColors.textColor }}
            />
          </div>
        </Field>

        {/* Country code + number joined into one control */}
        <Field label={copy?.phone_label || "Phone number"} error={phoneError} labelClass={t.label} labelStyle={labelStyle}>
          <div
            className={`${shell} flex items-stretch ${t.field} ${phoneError ? t.fieldError : ""}`}
            style={fieldStyles}
          >
            <CountryCodeDropdown
              value={countryCode}
              onChange={(code) => {
                setCountryCode(code);
                clearError("countryCode");
              }}
              embedded
              inputStyles={{ color: customColors.textColor }}
              customColors={customColors}
            />
            <span aria-hidden className={`my-2.5 w-px flex-shrink-0 ${t.divider}`} />
            <input
              className={`${textInput} flex-1 rounded-l-none`}
              placeholder={copy?.phone_placeholder || "Phone number"}
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(maskPhoneInput(e.target.value));
                clearError("phoneNumber", "countryCode");
              }}
              inputMode="tel"
              autoComplete="tel-national"
              style={{ color: customColors.textColor }}
            />
          </div>
        </Field>

        <Field
          label={copy?.email_label || "Email"}
          hint={emailOptional ? "Optional" : undefined}
          error={errors.email}
          labelClass={t.label}
          labelStyle={labelStyle}
        >
          <div className={`${shell} ${t.field} ${errors.email ? t.fieldError : ""}`} style={fieldStyles}>
            <input
              className={textInput}
              type="email"
              placeholder={copy?.email_placeholder || "you@example.com"}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError("email");
              }}
              autoComplete="email"
              inputMode="email"
              style={{ color: customColors.textColor }}
            />
          </div>
        </Field>

        <div className="pt-1">
          <button
            type="submit"
            className="h-11 w-full rounded-xl text-[15px] font-semibold tracking-[-0.01em] shadow-[0_1px_2px_rgba(16,24,40,0.12)] transition-[filter,transform] duration-150 ease-out hover:brightness-[1.06] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--uif-accent)]"
            style={buttonStyles}
          >
            {copy?.submit_label || "Continue"}
          </button>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className={`mt-2 h-9 w-full rounded-lg text-[13px] font-medium transition-colors ${t.skip} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--uif-accent)]`}
              style={{ color: customColors.secondaryTextColor }}
            >
              {copy?.skip_label || "Skip for now"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
